import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";
import { OnboardingTour } from "@/components/onboarding";
import { GlobalChatPopup } from "@/components/communication";
import { OnboardingTourProvider } from "@/hooks/useOnboardingTour";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useWorkspaceOnboarding } from "@/hooks/useWorkspaceOnboarding";
import { isClientPortalHost } from "@/lib/client-portal-host";

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchWorkspaces, workspaces, currentWorkspace, isLoading: isWorkspaceLoading } = useWorkspace();
  const { onboarding: workspaceOnboarding } = useWorkspaceOnboarding();
  const hasFetched = useRef(false);
  const isClientPortal = isClientPortalHost();
  const isClientWorkspace = isClientPortal || currentWorkspace?.role === "client";

  const isMindMapEditorPath =
    /^\/mapas-mentais\/[0-9a-fA-F-]{8,}$/.test(location.pathname);

  // Fetch workspaces on mount
  useEffect(() => {
    const init = async () => {
      if (!hasFetched.current) {
        hasFetched.current = true;
        await fetchWorkspaces();
      }
    };
    init();
  }, [fetchWorkspaces]);

  // Redirect to onboarding if no workspace
  useEffect(() => {
    // Only redirect if we have finished loading and there are no workspaces
    // We use a small timeout to avoid flashing if loading state changes quickly
    if (!isClientPortal && hasFetched.current && !isWorkspaceLoading && workspaces.length === 0) {
       navigate("/onboarding");
    }
  }, [isClientPortal, workspaces, isWorkspaceLoading, navigate]);

  useEffect(() => {
    if (!currentWorkspace || !workspaceOnboarding?.shouldShowWelcome) {
      return;
    }

    if (location.pathname !== "/welcome") {
      navigate("/welcome", { replace: true });
    }
  }, [currentWorkspace, workspaceOnboarding?.shouldShowWelcome, location.pathname, navigate]);

  if (isClientWorkspace) {
    const allowedClientPaths = ["/dashboard", "/calendario", "/conteudo", "/relatorios", "/aprovacoes"];
    const isAllowedPath = allowedClientPaths.includes(location.pathname);

    if (!isAllowedPath) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <SidebarProvider className="max-w-full overflow-x-hidden">
      <OnboardingTourProvider>
        <div className="flex h-screen w-full max-w-full overflow-hidden bg-background">
          <AppSidebar />
          <div className="flex h-screen min-w-0 max-w-full flex-1 flex-col overflow-hidden bg-muted/10 selection:bg-primary/20 selection:text-primary">
            <TopBar />
            <main
              className={[
                "flex-1 min-w-0 max-w-full overflow-x-hidden",
                isMindMapEditorPath
                  ? "min-h-0 overflow-hidden p-0"
                  : "overflow-y-auto px-4 py-4 sm:px-4 md:px-8 md:py-8",
              ].join(" ")}
            >
              <Outlet />
            </main>
          </div>
        </div>
        
        <OnboardingTour />
        {!isClientWorkspace && <GlobalChatPopup />}
      </OnboardingTourProvider>
    </SidebarProvider>
  );
}
